import { User } from "@/types/user";
import CryptoJS from "crypto-js";
import { findUserByUsername, updateUserPassword, updateUsername } from "@/services/supabaseService";

// JWT token oluşturma fonksiyonu
export const generateToken = (user: User): string => {
  const now = new Date();
  const expiresIn = 24 * 60 * 60 * 1000; // 1 gün (milisaniye cinsinden)
  const expirationTime = now.getTime() + expiresIn;
  
  const payload = {
    sub: user.id,
    username: user.username,
    role: user.role,
    exp: expirationTime,
    iat: now.getTime()
  };
  
  // JWT'nin basit bir implementasyonu (gerçek uygulamada daha güvenli bir kütüphane kullanılmalı)
  const base64Encode = (obj: any) => {
    return btoa(JSON.stringify(obj)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  };
  
  const header = {
    alg: "HS256",
    typ: "JWT"
  };
  
  const encodedHeader = base64Encode(header);
  const encodedPayload = base64Encode(payload);
  
  // Normalde secret key kullanarak HMAC-SHA256 ile imzalanır
  // Burada basit bir simulasyon yapıyoruz
  const jwtSecret = import.meta.env.VITE_JWT_SECRET || 'super-gizli-anahtar';
  const signature = CryptoJS.HmacSHA256(
    `${encodedHeader}.${encodedPayload}`,
    jwtSecret
  ).toString(CryptoJS.enc.Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  
  return `${encodedHeader}.${encodedPayload}.${signature}`;
};

// Token çözme ve doğrulama
export const verifyToken = (token: string): User | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const base64Decode = (str: string) => {
      // Base64 URL güvenli formatı düzeltme
      const normalizedStr = str
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      
      // Base64 padding ekleme
      const padding = normalizedStr.length % 4;
      const paddedStr = padding ? 
        normalizedStr + '='.repeat(4 - padding) : 
        normalizedStr;
      
      return JSON.parse(atob(paddedStr));
    };
    
    const payload = base64Decode(parts[1]);
    const now = new Date().getTime();
    
    // Token süresi dolmuş mu kontrol et
    if (payload.exp && payload.exp < now) {
      return null;
    }
    
    return {
      id: payload.sub,
      username: payload.username,
      role: payload.role
    };
  } catch (error) {
    console.error("Token çözme hatası:", error);
    return null;
  }
};

// Şifreyi hashleme fonksiyonu
export const hashPassword = (password: string): string => {
  return CryptoJS.SHA256(password).toString(CryptoJS.enc.Hex);
};

// Şifre doğrulama
export const verifyPassword = (plainPassword: string, hashedPassword: string): boolean => {
  const hashedInput = CryptoJS.SHA256(plainPassword).toString(CryptoJS.enc.Hex);
  return hashedInput === hashedPassword;
};

// Kalan token süresini kontrol et (milisaniye cinsinden)
export const getTokenTimeRemaining = (token: string): number => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return 0;
    
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(parts[1].length + (4 - parts[1].length % 4) % 4, '=')));
    const now = new Date().getTime();
    
    return Math.max(0, payload.exp - now);
  } catch (error) {
    return 0;
  }
};

// Kullanıcıyı veritabanında bul
export const findUser = async (username: string): Promise<(User & { password: string }) | null> => {
  try {
    const userData = await findUserByUsername(username);
    if (!userData) return null;
    
    return {
      id: userData.id,
      username: userData.username,
      role: userData.role,
      password: userData.password
    };
  } catch (error) {
    console.error("Kullanıcı bulma hatası:", error);
    return null;
  }
};

// Kullanıcı şifresini güncelle
export const changeUserPassword = async (userId: string, newPassword: string): Promise<boolean> => {
  try {
    const newPasswordHash = hashPassword(newPassword);
    return await updateUserPassword(userId, newPasswordHash);
  } catch (error) {
    console.error("Şifre değiştirme hatası:", error);
    return false;
  }
};

// Kullanıcı adını güncelle
export const changeUsername = async (userId: string, newUsername: string): Promise<{ success: boolean; message: string; data?: any }> => {
  try {
    return await updateUsername(userId, newUsername);
  } catch (error) {
    console.error("Kullanıcı adı değiştirme hatası:", error);
    return { success: false, message: "Beklenmeyen bir hata oluştu." };
  }
}; 